import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Shield, Lightbulb, Award, Users, Heart } from 'lucide-react';
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const TEAM_IMAGE = "https://images.pexels.com/photos/7580644/pexels-photo-7580644.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const OFFICE_IMAGE = "https://images.pexels.com/photos/7653461/pexels-photo-7653461.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const About = () => {
  const values = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Integrity",
      description: "We build enduring partnerships rooted in trust, transparency, and unwavering accountability."
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Excellence",
      description: "We take immense pride in our work, consistently delivering unparalleled results."
    },
    {
      icon: <Lightbulb className="w-6 h-6" />,
      title: "Innovation",
      description: "We think outside the box, using cutting-edge technology and creative strategies."
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Hustle",
      description: "'Outworked' isn't in our vocabulary. We never quit until we achieve the extraordinary."
    }
  ];

  const milestones = [
    { year: "2010", title: "Founded", description: "Prestigious Marketing Inc was born with a vision to revolutionize marketing." },
    { year: "2015", title: "Expansion", description: "Expanded services to include digital advertising and CRM solutions." },
    { year: "2019", title: "OPC Leadership", description: "Became a leading provider of timeshare OPC marketing services." },
    { year: "2024", title: "AI Innovation", description: "Launched Ghost AI Assistant, pioneering AI-driven marketing." }
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="container-max mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
                About Us
              </span>
              <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 mb-6">
                Architects of Success
              </h1>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                At Prestigious Marketing Inc, we are architects of success, forging a future 
                where data-driven brilliance and unstoppable innovation converge. Our mission 
                is to propel businesses beyond the ordinary, to heights they never imagined possible.
              </p>
              <p className="text-slate-600 leading-relaxed">
                We're more than marketers; we're visionary strategists, wielding data like an 
                art form to craft bespoke campaigns that mesmerize and outperform the rest.
              </p>
            </div>
            <div className="relative">
              <img 
                src={OFFICE_IMAGE}
                alt="Our office"
                className="rounded-2xl shadow-xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#1E3A8A] text-white p-6 rounded-xl shadow-lg">
                <div className="text-4xl font-black mb-1">15+</div>
                <div className="text-sm opacity-90">Years of Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
              Our Mission
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-8">
              Your Vision, Our Strategies, We Win Together
            </h2>
            <div className="bg-slate-50 rounded-2xl p-8 lg:p-12">
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                With our unwavering commitment to excellence, we take the lead in the pursuit 
                of perfection. From dawn till dusk, and every moment in between, we vigilantly 
                optimize, refine, and fine-tune to ensure nothing is left to chance.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed">
                Our clients' triumphs are not mere probabilities; they are certainties etched 
                in the bedrock of our dedication. Embrace the thrill of limitless possibilities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-slate-50" data-testid="values-section">
        <div className="container-max mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
              Our Values
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              What Drives Us Forward
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => (
              <Card key={idx} className="border-slate-200 bg-white card-hover" data-testid={`value-card-${idx}`}>
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-[#1E3A8A] text-white rounded-2xl flex items-center justify-center mx-auto mb-5">
                    {value.icon}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src={TEAM_IMAGE}
                alt="Our team"
                className="rounded-2xl shadow-lg w-full"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
                Our Team
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6">
                Trailblazers Who Shatter Boundaries
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                At Prestigious Marketing Inc, we're not your ordinary team – we're trailblazers 
                who shatter boundaries and redefine success. Think outside of the box? It's our specialty!
              </p>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Armed with the 'Hustle' mentality, our group of passionate experts turns ambition 
                into action, leaving no stone unturned to make your dreams a thriving reality.
              </p>
              <Link to="/careers">
                <Button 
                  className="bg-[#1E3A8A] hover:bg-[#1e40af]"
                  data-testid="join-team-btn"
                >
                  Join Our Team
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="section-padding bg-[#0F172A]" data-testid="timeline-section">
        <div className="container-max mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4 block">
              Our Journey
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
              Building Excellence Over Time
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {milestones.map((milestone, idx) => (
              <div key={idx} className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <div className="text-[#EA580C] text-3xl font-black mb-3">
                  {milestone.year}
                </div>
                <h3 className="text-white text-lg font-bold mb-2">
                  {milestone.title}
                </h3>
                <p className="text-slate-400 text-sm">
                  {milestone.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6">
            Ready to Join Our Success Story?
          </h2>
          <p className="text-slate-600 mb-10 max-w-2xl mx-auto">
            Whether you're looking to grow your business or join our team, 
            we're ready to make extraordinary things happen together.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/partner">
              <Button 
                size="lg" 
                className="bg-[#EA580C] hover:bg-[#c2410c] text-white px-8"
                data-testid="about-cta-partner"
              >
                Become a Partner
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline"
                className="border-slate-300 text-slate-700 hover:bg-slate-100 px-8"
                data-testid="about-cta-contact"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
